import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Button, ButtonGroup } from 'react-bootstrap';
import { Edit } from 'react-feather';
import VirtualList from '../components/VirtualList';
import LoadingSpinner from '../../InitialPage/Sidebar/LoadingSpinner';
import { isHeaderVisible } from 'Core/redux/main/selectors';
import { selectReceivingFilterVisible } from './store/selectors';
import { getAsnSourceFileName, isAsnOriginShipment } from './mappings-asn';

const asnBadgeStyle = {
  backgroundColor: '#02a8b5',
  color: '#fff',
};

const MappingsTile = ({ shipments = [], loading = false, onViewShipment }) => {
  const [columnCount, setColumnCount] = useState(6);
  const [columnWidth, setColumnWidth] = useState(300);
  const tileRef = useRef(null);
  const filterVisible = useSelector(selectReceivingFilterVisible);
  const headerVisible = useSelector(isHeaderVisible);

  const updateColumnSettings = useCallback(() => {
    if (tileRef.current) {
      const width = tileRef.current.offsetWidth;
      let columns = 6;
      if (width < 576) {
        columns = 1;
      } else if (width < 768) {
        columns = 3;
      } else if (width < 992) {
        columns = 5;
      } else {
        columns = 6;
      }
      setColumnCount(columns);
      setColumnWidth(width / columns);
    }
  }, []);

  useEffect(() => {
    updateColumnSettings();
    window.addEventListener('resize', updateColumnSettings);
    return () => window.removeEventListener('resize', updateColumnSettings);
  }, [updateColumnSettings]);

  const itemData = shipments.map(shipment => (
    <MappingsTileItem shipment={shipment} key={shipment.receivingShipmentID} onViewShipment={onViewShipment} />
  ));
  const rowCount = Math.ceil(shipments.length / columnCount) || 0;

  return (
    <div
      className={`shipment-tile-body ${filterVisible ? 'filter-visible' : ''} ${!headerVisible ? 'header-collapsed' : ''}`}
      ref={tileRef}
      style={{ position: 'relative' }}
    >
      {loading && <LoadingSpinner />}
      {
        <VirtualList
          columnCount={columnCount}
          rowCount={rowCount}
          itemData={itemData}
          columnWidth={columnWidth}
          rowHeight={185}
        />
      }
    </div>
  );
};

const MappingsTileItem = ({ shipment, onViewShipment }) => {
  const handleView = e => {
    e.preventDefault();
    e.stopPropagation();
    if (onViewShipment) onViewShipment(shipment);
  };

  return (
    <div key={shipment.receivingShipmentID} className="shipment-tile">
      <div
        className={`card p-3 m-2 ${shipment.isNewlyCreated ? 'border-success' : ''}`}
        style={{
          border: '1px solid #ddd',
          borderRadius: '4px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        }}
      >
        <div className="tile-actions">
          <ButtonGroup size="sm">
            <Button type="button" variant="light" onClick={handleView}>
              <Edit size={16} />
            </Button>
          </ButtonGroup>
        </div>
        <div className="d-flex justify-content-between">
          <div className="d-flex flex-row align-items-center">
            <div className="ms-2 c-details">
              <h6 className="mb-0">{shipment.shipmentNumber}</h6>
            </div>
          </div>
        </div>

        <div className="mt-2 d-flex flex-wrap gap-2">
          <span
            className="badge"
            title={shipment.status || 'N/A'}
            style={{
              backgroundColor: shipment.backgroundColor || '#e0e0e0',
              color: shipment.textColor || '#000000',
            }}
          >
            {shipment.status || 'N/A'}
          </span>
          {isAsnOriginShipment(shipment) ? (
            <span className="badge" style={asnBadgeStyle} title={getAsnSourceFileName(shipment)}>
              ASN
            </span>
          ) : null}
          <span
            className="badge"
            style={{
              backgroundColor: 'transparent',
              color: '#000000',
            }}
          >
            {shipment.shipmentDate || 'N/A'}
          </span>
        </div>

        <div className="mt-2 d-flex flex-wrap gap-2">
          <span
            className="badge"
            style={{
              backgroundColor: 'transparent',
              color: '#f0b429',
            }}
          >
            Appt: {shipment.appointmentDateTime || 'N/A'}
          </span>
        </div>

        <div className="mt-2" style={{ minWidth: 0, maxWidth: '100%' }}>
          <span
            className="badge"
            title={shipment.supplierName || ''}
            style={{
              backgroundColor: 'transparent',
              color: '#5bc0de',
              display: 'block',
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textAlign: 'left',
            }}
          >
            {shipment.supplierName || ''}
          </span>
        </div>
      </div>
    </div>
  );
};

export default MappingsTile;
